import { createContext, useCallback, useContext, useRef } from 'react';

const FormAutofillContext = createContext(null);

// --- util: compose multiple refs into one ---
function composeRefs(...refs) {
    return (el) => {
        refs.forEach((r) => {
            if (!r) return;
            if (typeof r === 'function') r(el);
            else r.current = el;
        });
    };
}

export function FormAutofillProvider({ debounceMs = 50, children }) {
    const entriesRef = useRef(new Set());    // Set<{ ref: HTMLElement|null, callback: (el) => void }>
    const debounceTimeoutRef = useRef(null);

    const value = { entriesRef, debounceTimeoutRef, debounceMs };
    return (
        <FormAutofillContext.Provider value={value}>
            {children}
        </FormAutofillContext.Provider>
    );
}

export function useAutofillNotifier() {
    const ctx = useContext(FormAutofillContext);
    if (!ctx) {
        throw new Error('useAutofillNotifier must be used within a FormAutofillProvider');
    }

    const { entriesRef, debounceTimeoutRef, debounceMs } = ctx;

    const notifyAll = useCallback((e) => {
        for (const { ref, callback } of entriesRef.current) {
            if (ref && typeof callback === 'function') callback(ref, e);
        }
    }, [entriesRef]);

    const onAnimationStartCapture = useCallback((e) => {
        if (e.animationName === 'autofill-start') {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            e.persist();
            debounceTimeoutRef.current = setTimeout(() => notifyAll(e), debounceMs);
        }
    }, [notifyAll, debounceMs, debounceTimeoutRef]);

    /**
     * createAutofillProps(callback, externalRef?)
     * - callback(el): called after a debounced autofill batch
     * - externalRef: optional ref from the component/parent (callback ref or object ref)
     */
    const createAutofillProps = useCallback((callback, ...externalRefs) => {
        const entry = { ref: null, callback };

        // internal ref the hook controls
        const hookRef = (el) => {
            entry.ref = el;
            if (el) entriesRef.current.add(entry);
            else entriesRef.current.delete(entry);
        };

        // compose the hook’s ref with the caller’s ref
        const ref = composeRefs(...externalRefs, hookRef);

        return { ref, onAnimationStartCapture };
    }, [entriesRef, onAnimationStartCapture]);

    return { createAutofillProps };
}